import React, { useState, useEffect, useRef } from 'react';
import { Search, ArrowRight, ExternalLink, Sparkles, CheckCircle2, Star, Zap, X, Heart, Share2, History, Clock, Crown, Flame, Monitor, AlertCircle, Cookie, PlayCircle, TrendingUp, LayoutGrid, Table as TableIcon, Copy, AlertTriangle, Lock, CreditCard, Check, Globe } from 'lucide-react';
import { findTools } from './services/gemini';
import { ToolResult, SearchFilter, SearchHistoryItem, TickerItem } from './types';
import LegalModal from './components/LegalModals';

// SEO Growth Engine: Database of High-Value Keywords (Expanded to 100+)
const seoKeywords = [
  "Free PDF Editor", "Remove Background Video", "AI Image Upscaler", "Audio Noise Reduction", 
  "Convert HEIC to JPG", "Free Video Compressor", "Text to Speech AI", "Logo Generator Free", 
  "Resume Builder No Signup", "Screen Recorder Web", "Video Trimmer Online", "AI Avatar Generator", 
  "Remove Object from Photo", "Colorize Black and White Photo", "QR Code Generator Free", "Youtube Thumbnail Downloader", 
  "Font Identifier", "Markdown Editor Online", "JSON Formatter", "Base64 Decoder", "Lorem Ipsum Generator", 
  "CSS Gradient Generator", "SVG Optimizer", "Favicon Generator", "Social Media Video Downloader", 
  "Instagram Story Saver", "TikTok Watermark Remover", "Twitter Video Downloader", "Spotify to MP3", 
  "Gif Maker from Video", "Meme Generator No Watermark", "AI Writer Free", "Paraphrasing Tool", "Grammar Checker", 
  "Plagiarism Checker Free", "Code Formatter", "SQL Formatter", "Regex Tester", "Cron Job Generator", 
  "UUID Generator", "Password Generator", "Network Speed Test", "Ping Test", "IP Lookup", 
  "Whois Lookup", "Domain Availability Search", "SSL Checker", "DNS Propagation Check", "Port Scanner", "Subnet Calculator",
  "Unit Converter", "Currency Converter", "Time Zone Converter", "Epoch Converter", "Binary Translator",
  "Hex to Decimal", "ASCII Art Generator", "Emoji Copy Paste", "Symbol Copy Paste", "Kaomoji", "Zalgo Text Generator",
  "Convert WEBP to PNG", "Extract Audio from Video", "Merge PDF Free", "Split PDF Online", "Compress Image TinyPNG",
  "Vectors to SVG", "Color Picker from Image", "HTML Encoder Decoder", "URL Encoder Decoder", "JWT Debugger",
  "Linux Command Cheat Sheet", "Git Command Cheat Sheet", "Docker Compose Generator", "Nginx Config Generator",
  "Htaccess Redirect Generator", "Meta Tag Generator", "Open Graph Tester", "Twitter Card Validator",
  "Website Speed Test", "Broken Link Checker", "XML Sitemaps Generator", "Robots.txt Generator",
  "Diff Checker Text", "Character Counter", "Word Counter", "Case Converter", "Remove Duplicate Lines",
  "Sort List Alphabetically", "Random Number Generator", "Flip Coin Online", "Roll Dice Online",
  "Stopwatch Online", "Timer Online", "Metronome Online", "Guitar Tuner Online", "BPM Counter"
];

const App = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ToolResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activeFilter, setActiveFilter] = useState<SearchFilter['id']>('all');
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  
  // Gap Analysis & Monetization Features
  const [streak, setStreak] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [savedTools, setSavedTools] = useState<string[]>([]);
  const [showVideoModal, setShowVideoModal] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  
  // Paywall / Stripe State
  const [freeSearches, setFreeSearches] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState<'limit' | 'upgrade'>('upgrade');

  const FREE_SEARCH_LIMIT = 3;
  const STRIPE_LINK = "https://buy.stripe.com/cNi00j5tO7Eyggm6LTaR203";

  // Legal Modals State
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | 'about' | null>(null);
  
  const resultsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // SEO: Daily Keyword Rotation Logic
  const getDailyKeywords = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    const dayOfYear = Math.floor(diff / oneDay);
    
    // Select 24 keywords based on the day of the year (Deterministic Rotation)
    const count = 24;
    const startIndex = (dayOfYear * 5) % seoKeywords.length; 
    
    let selection = [];
    for(let i = 0; i < count; i++) {
        selection.push(seoKeywords[(startIndex + i) % seoKeywords.length]);
    }
    return selection;
  };

  const dailyKeywords = getDailyKeywords();

  const filters: SearchFilter[] = [
    { id: 'all', label: 'Best Match', promptModifier: '' },
    { id: 'free', label: 'Strictly Free', promptModifier: 'Must be 100% Free forever' },
    { id: 'no-signup', label: 'No Sign-up', promptModifier: 'Must not require login/registration' },
    { id: 'opensource', label: 'Open Source', promptModifier: 'Must be Open Source' },
  ];

  const performSearch = async (searchQuery: string, isInitialLoad = false) => {
    if (!searchQuery.trim()) return;

    // Strict Limit Check (Loophole Fix)
    const currentCount = parseInt(localStorage.getItem('sitefinder_search_count') || '0');
    const isPrem = localStorage.getItem('sitefinder_premium') === 'true';

    // If not premium, and limit reached, and not just refreshing a page (unless initial load via deep link should also count? 
    // Usually initial load via deep link is allowed ONCE if within limit, but if limit is 0/3, we must block.)
    if (!isPrem && currentCount >= FREE_SEARCH_LIMIT && !isInitialLoad) {
      setPremiumModalReason('limit');
      setShowPremiumModal(true);
      return;
    }

    setLoading(true);
    setSearched(true);
    setResults([]); 
    setViewMode('grid');
    setQuery(searchQuery);
    
    // SEO Deep Link Update
    const newUrl = `${window.location.pathname}?q=${encodeURIComponent(searchQuery)}`;
    if (window.location.search !== `?q=${encodeURIComponent(searchQuery)}`) {
        window.history.pushState({ path: newUrl }, '', newUrl);
    }
    
    if (!isPrem && !isInitialLoad) {
      const newCount = currentCount + 1;
      setFreeSearches(newCount);
      localStorage.setItem('sitefinder_search_count', newCount.toString());
    }
    
    const currentFilter = filters.find(f => f.id === activeFilter);
    const foundTools = await findTools(searchQuery, currentFilter?.promptModifier);
    
    setResults(foundTools);
    setLoading(false);

    if (foundTools.length > 0 && !isInitialLoad) triggerConfetti();

    // Update History
    const savedHistoryStr = localStorage.getItem('toolscout_history');
    const existingHistory = savedHistoryStr ? JSON.parse(savedHistoryStr) : [];
    
    const newHistoryItem: SearchHistoryItem = {
      query: searchQuery,
      timestamp: Date.now(),
      topResult: foundTools[0]?.name
    };
    
    const updatedHistory = [newHistoryItem, ...existingHistory.filter((h: SearchHistoryItem) => h.query !== searchQuery)].slice(0, 10);
    setHistory(updatedHistory);
    localStorage.setItem('toolscout_history', JSON.stringify(updatedHistory));
  };

  // --- Initialization ---
  useEffect(() => {
    // 1. Stripe Payment Success
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const urlQuery = urlParams.get('q');

    if (sessionId) {
      localStorage.setItem('sitefinder_premium', 'true');
      setIsPremium(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      alert("Welcome to Premium! Your unlimited access is active.");
    }

    // 2. Load Premium Status
    const premiumStatus = localStorage.getItem('sitefinder_premium');
    if (premiumStatus === 'true') setIsPremium(true);

    // 3. Monthly Reset Logic
    const storedMonth = localStorage.getItem('sitefinder_search_month');
    const currentMonth = `${new Date().getMonth() + 1}-${new Date().getFullYear()}`;

    if (storedMonth !== currentMonth) {
        setFreeSearches(0);
        localStorage.setItem('sitefinder_search_count', '0');
        localStorage.setItem('sitefinder_search_month', currentMonth);
    } else {
        const searchCount = localStorage.getItem('sitefinder_search_count');
        if (searchCount) setFreeSearches(parseInt(searchCount));
    }

    // 4. Load Data
    const savedHistory = localStorage.getItem('toolscout_history');
    if (savedHistory) setHistory(JSON.parse(savedHistory));
    const savedCollection = localStorage.getItem('toolscout_saved');
    if (savedCollection) setSavedTools(JSON.parse(savedCollection));

    // 5. Streak
    const lastVisit = localStorage.getItem('toolscout_last_visit');
    const currentStreak = parseInt(localStorage.getItem('toolscout_streak') || '0');
    const today = new Date().toDateString();

    if (lastVisit !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      if (lastVisit === yesterday.toDateString()) {
        const newStreak = currentStreak + 1;
        setStreak(newStreak);
        localStorage.setItem('toolscout_streak', newStreak.toString());
      } else {
        setStreak(1);
        localStorage.setItem('toolscout_streak', '1');
      }
      localStorage.setItem('toolscout_last_visit', today);
    } else {
      setStreak(currentStreak);
    }

    if (!localStorage.getItem('toolscout_consent')) {
      setTimeout(() => setShowCookieConsent(true), 2000);
    }

    // 6. Handle SEO Deep Link (Auto-Search with STRICT CHECK)
    if (urlQuery) {
        const currentCount = parseInt(localStorage.getItem('sitefinder_search_count') || '0');
        const isPrem = localStorage.getItem('sitefinder_premium') === 'true';

        // Fix Loophole: Check limit even for deep links
        if (!isPrem && currentCount >= FREE_SEARCH_LIMIT) {
            setQuery(urlQuery);
            setPremiumModalReason('limit');
            setShowPremiumModal(true);
        } else {
            performSearch(urlQuery, true);
        }
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowHistory(false);
        setShowVideoModal(null);
        setShowPremiumModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const tickerItems: TickerItem[] = [
    { location: "Berlin", tool: "Compress 50MB PDF to 1MB", time: "just now" },
    { location: "Austin", tool: "Remove object from video", time: "4s ago" },
    { location: "Seoul", tool: "Convert HEIC for elderly parents", time: "8s ago" },
    { location: "London", tool: "Free alternative to Midjourney", time: "12s ago" },
    { location: "Toronto", tool: "Fix grainy audio recording", time: "22s ago" },
    { location: "Mumbai", tool: "Upscale 480p anime", time: "30s ago" },
  ];

  // Duplicate ticker items for smoother loop
  const seamlessTickerItems = [...tickerItems, ...tickerItems];

  useEffect(() => {
    if (results.length > 0) {
      const titleText = `${query} - Top AI Tools | SiteFinder`;
      const descriptionText = `Top ${results.length} tools for "${query}". Compare pricing, features, and alternatives. AI-curated list updated for 2024.`;

      document.title = titleText;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', descriptionText);

      const schemaData = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://sitefinder.app" },
              { "@type": "ListItem", "position": 2, "name": "Search", "item": `https://sitefinder.app/?q=${encodeURIComponent(query)}` }
            ]
          },
          {
            "@type": "ItemList",
            "name": `Top Tools for "${query}"`,
            "description": descriptionText,
            "itemListElement": results.map((tool, index) => ({
              "@type": "SoftwareApplication",
              "position": index + 1,
              "name": tool.name,
              "applicationCategory": tool.category,
              "operatingSystem": tool.platforms.join(", "),
              "description": tool.description,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": (tool.trustScore / 20).toFixed(1),
                "reviewCount": tool.reviews || 0
              },
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "availability": tool.pricing === 'Free' ? "https://schema.org/OnlineOnly" : undefined
              }
            }))
          }
        ]
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schemaData);
      document.head.appendChild(script);
      
      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
        document.title = "SiteFinder.app - Stop Searching, Start Finding";
      };
    }
  }, [results, query]);

  const triggerConfetti = () => {
    setShowConfetti(true);
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]); 
    playSuccessSound();
    setTimeout(() => setShowConfetti(false), 3000);
  };

  const playSuccessSound = () => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(500, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1000, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.5);
    } catch (e) { /* Ignore audio context errors */ }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(query);
  };

  const toggleSave = (url: string) => {
    const isSaved = savedTools.includes(url);
    const newSaved = isSaved ? savedTools.filter(u => u !== url) : [...savedTools, url];
    setSavedTools(newSaved);
    localStorage.setItem('toolscout_saved', JSON.stringify(newSaved));
    if (!isSaved && navigator.vibrate) navigator.vibrate(50);
  };

  const shareStack = async () => {
    const stackText = "Here are my saved tools from SiteFinder.app:\n" + savedTools.join('\n');
    try {
        await navigator.clipboard.writeText(stackText);
        alert("Your tool stack has been copied to clipboard! Ready to paste into Slack/Discord.");
    } catch (err) {
        console.error("Failed to copy", err);
    }
  };

  const shareResult = async (tool: ToolResult) => {
    const shareData = {
      title: `Found: ${tool.name} on SiteFinder`,
      text: tool.description,
      url: tool.url
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        throw new Error("Share API not supported");
      }
    } catch (err) {
      navigator.clipboard.writeText(tool.url);
      alert(`Link to ${tool.name} copied to clipboard!`);
    }
  };

  const shareSearch = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Search: ${query}`, url });
      } else {
        throw new Error("Share API not supported");
      }
    } catch (err) {
      navigator.clipboard.writeText(url);
      alert("Search page link copied to clipboard!");
    }
  };

  useEffect(() => {
    if (results.length > 0 && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [results]);

  const restoreSearch = (histItem: SearchHistoryItem) => {
    setQuery(histItem.query);
    setShowHistory(false);
    performSearch(histItem.query);
  };

  const users = [
    "https://i.pravatar.cc/150?u=a042581f4e29026024d",
    "https://i.pravatar.cc/150?u=a042581f4e29026704d",
    "https://i.pravatar.cc/150?u=a04258114e29026302d",
    "https://i.pravatar.cc/150?u=a048581f4e29026701d",
  ];

  return (
    <div className="min-h-screen relative font-sans text-slate-200 flex flex-col overflow-x-hidden">
      
      <div className="fixed inset-0 bg-background overflow-hidden pointer-events-none">
        <div className="aurora-blob w-[500px] h-[500px] bg-primary-600 rounded-full -top-40 -left-20 mix-blend-screen opacity-20 animate-pulse-slow"></div>
        <div className="aurora-blob w-[600px] h-[600px] bg-accent-cyan rounded-full top-20 right-0 mix-blend-screen opacity-10 animate-pulse-slow delay-1000"></div>
        <div className="aurora-blob w-[800px] h-[400px] bg-primary-500 rounded-full bottom-0 left-1/4 mix-blend-screen opacity-10 blur-[100px]"></div>
      </div>

      {showConfetti && (
        <>
          <div className="confetti left-[20%]" style={{backgroundColor: '#a78bfa'}}></div>
          <div className="confetti left-[40%]" style={{backgroundColor: '#22d3ee', animationDelay: '0.2s'}}></div>
          <div className="confetti left-[60%]" style={{backgroundColor: '#f472b6', animationDelay: '0.5s'}}></div>
          <div className="confetti left-[80%]" style={{backgroundColor: '#8b5cf6', animationDelay: '0.1s'}}></div>
        </>
      )}

      {showCookieConsent && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 w-[90%] max-w-md animate-slide-up">
          <div className="bg-surface-900/90 backdrop-blur-md border border-white/10 p-4 rounded-xl flex items-center justify-between shadow-2xl">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5 text-amber-500" />
              <p className="text-xs text-slate-300">Privacy First: We only use LocalStorage.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { localStorage.setItem('toolscout_consent', 'true'); setShowCookieConsent(false); }} className="px-3 py-1 bg-white text-black text-xs font-bold rounded-lg hover:bg-slate-200">OK</button>
            </div>
          </div>
        </div>
      )}

      {/* PREMIUM PAYWALL MODAL */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl animate-fade-in font-sans">
          <div className="bg-gradient-to-br from-surface-900 to-[#1a182e] border border-primary-500/30 rounded-2xl w-full max-w-md shadow-2xl relative overflow-hidden flex flex-col text-center">
            
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 via-accent-pink to-accent-cyan"></div>
            <div className="absolute top-[-50px] right-[-50px] w-32 h-32 bg-primary-500/20 blur-[60px] rounded-full"></div>

            <button onClick={() => setShowPremiumModal(false)} className="absolute top-4 right-4 p-2 text-slate-500 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 pb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-500/30">
                <Crown className="w-8 h-8 text-white fill-white/20" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">
                {premiumModalReason === 'limit' ? "Monthly Limit Reached" : "Unlock SiteFinder Pro"}
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                 {premiumModalReason === 'limit' 
                   ? `You've used all ${FREE_SEARCH_LIMIT} free searches for this month. Upgrade now to keep finding.`
                   : "Get unlimited searches, comparison tables, and save unlimited tools to your collection."}
              </p>

              <div className="space-y-3 mb-8 text-left bg-white/5 p-4 rounded-xl border border-white/5">
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Unlimited AI Searches</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Compare Tables & Pricing</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Save Unlimited Collections</span>
                </div>
              </div>

              <a 
                href={STRIPE_LINK} 
                className="w-full block bg-white text-black py-4 rounded-xl font-bold text-base hover:bg-slate-100 transition-all shadow-xl shadow-primary-500/10 hover:shadow-primary-500/20 hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                Unlock for $3.99/mo <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-[10px] text-slate-500 mt-4">Secure payment via Stripe. Cancel anytime.</p>
            </div>
          </div>
        </div>
      )}

      {/* History Sidebar */}
      <div className={`fixed inset-y-0 right-0 w-80 bg-surface-900/95 backdrop-blur-xl border-l border-white/10 z-50 transform transition-transform duration-300 ease-in-out ${showHistory ? 'translate-x-0' : 'translate-x-full'} shadow-2xl`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-8">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <History className="w-4 h-4 text-primary-400" />
              History & Saved
            </h3>
            <button onClick={() => setShowHistory(false)} className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-grow overflow-y-auto space-y-4 custom-scrollbar">
            {history.length === 0 ? (
              <div className="text-center text-slate-500 mt-10 text-sm">
                No history yet.
              </div>
            ) : (
              history.map((item, i) => (
                <div key={i} onClick={() => restoreSearch(item)} className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-primary-500/30 cursor-pointer group transition-all">
                  <p className="text-white font-medium text-sm mb-1 group-hover:text-primary-300 transition-colors">{item.query}</p>
                  {item.topResult && <p className="text-xs text-slate-500 flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" /> Found: {item.topResult}</p>}
                  <p className="text-[10px] text-slate-600 mt-2 text-right">{new Date(item.timestamp).toLocaleDateString()}</p>
                </div>
              ))
            )}
             
            {savedTools.length > 0 && (
                <div className="mt-8 pt-4 border-t border-white/5">
                    <div className="flex justify-between items-center mb-4">
                        <p className="text-xs text-slate-400">You have {savedTools.length} saved tools.</p>
                        <button onClick={shareStack} className="flex items-center gap-1 text-[10px] bg-primary-500/10 text-primary-400 px-2 py-1 rounded hover:bg-primary-500/20 transition-colors">
                            <Copy className="w-3 h-3"/> Share Stack
                        </button>
                    </div>
                </div>
            )}
          </div>
        </div>
      </div>

      {showHistory && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={() => setShowHistory(false)} />}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
             <div className="bg-surface-900 border border-white/10 rounded-2xl w-full max-w-2xl aspect-video flex flex-col items-center justify-center relative shadow-2xl">
                 <button onClick={() => setShowVideoModal(null)} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20"><X className="w-5 h-5 text-white"/></button>
                 <PlayCircle className="w-16 h-16 text-primary-500 opacity-50 mb-4" />
                 <p className="text-white font-medium">Simulated Video Demo for {showVideoModal}</p>
                 <p className="text-slate-400 text-sm mt-2">In a production app, this would play a 15s clip.</p>
             </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="relative z-20 px-6 py-5 border-b border-white/5 bg-background/50 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer group" onClick={() => { setSearched(false); setQuery(''); window.history.pushState({}, '', '/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:scale-105 transition-transform">
              <Sparkles className="w-4 h-4 text-white fill-white" />
            </div>
            <span className="font-semibold text-white tracking-tight text-lg">SiteFinder</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
            {/* Free Search Counter */}
            {!isPremium && (
                <span className="text-xs text-slate-500 border border-white/5 px-3 py-1 rounded-full">
                    {freeSearches}/{FREE_SEARCH_LIMIT} Free Searches
                </span>
            )}
            
            <button onClick={() => setLegalModal('about')} className="hover:text-white transition-colors">About</button>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 cursor-help" title="Visit daily to keep your streak!">
              <Flame className="w-3.5 h-3.5 fill-current" />
              <span className="text-xs font-bold">{streak} Day Streak</span>
            </div>
            {isPremium ? (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-300">
                <Crown className="w-3.5 h-3.5 fill-current" />
                <span className="text-xs font-bold">PRO</span>
              </div>
            ) : (
              <button 
                onClick={() => { setPremiumModalReason('upgrade'); setShowPremiumModal(true); }} 
                className="text-xs font-medium text-slate-300 hover:text-white flex items-center gap-1"
              >
                <Lock className="w-3 h-3" /> Upgrade
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowHistory(true)}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-full transition-all relative"
            >
              <History className="w-5 h-5" />
              {savedTools.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full border border-background"></span>}
            </button>
          </div>
        </div>
      </nav>

      <main className="relative z-10 flex-grow max-w-5xl mx-auto px-6 flex flex-col items-center w-full">
        
        {/* Hero Section */}
        <div className={`transition-all duration-700 ease-out w-full flex flex-col items-center ${searched ? 'mt-8 mb-8' : 'mt-20 md:mt-32'}`}>
          
          {!searched && (
            <div className="text-center mb-10 animate-fade-in max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-primary-300 mb-6 hover:bg-white/10 transition-colors cursor-default">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
                Index updated: 1,240 new AI tools added today
              </div>
              <h1 className="text-5xl md:text-7xl font-semibold text-white mb-6 tracking-tight leading-[1.1]">
                Stop searching. <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-cyan">Start finding.</span>
              </h1>
              <p className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-xl mx-auto">
                Describe your problem naturally. We scout the hidden gems of the web to find the exact tool you need.
              </p>
            </div>
          )}

          {/* Search Box */}
          <div className={`w-full transition-all duration-700 ${searched ? 'max-w-2xl' : 'max-w-xl'}`}>
            <form onSubmit={handleSearchSubmit} className="relative group z-30">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-accent-cyan rounded-2xl opacity-30 group-hover:opacity-60 blur transition duration-500"></div>
              <div className="relative flex items-center glass-input rounded-2xl p-2 transition-all duration-300 bg-background/80">
                <Search className={`w-6 h-6 ml-4 transition-colors ${loading ? 'text-primary-400' : 'text-slate-500'}`} />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g., I need to compress a 4GB video for free..."
                  className="w-full bg-transparent text-white placeholder-slate-500 px-4 py-4 text-lg outline-none"
                  autoFocus
                />
                <div className="hidden md:flex items-center mr-2">
                  <span className="px-2 py-1 rounded bg-white/10 text-[10px] text-slate-500 border border-white/5 font-mono">/</span>
                </div>
                <button 
                  type="submit"
                  disabled={loading || !query}
                  className="bg-white hover:bg-slate-100 text-black px-6 py-3 rounded-xl font-medium transition-all duration-200 disabled:opacity-0 disabled:translate-x-4 transform flex items-center gap-2 shadow-lg shadow-white/10"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </form>

            <div className={`mt-4 flex flex-wrap justify-center gap-2 transition-all duration-500 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setActiveFilter(filter.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 flex items-center gap-1.5 ${
                    activeFilter === filter.id 
                      ? 'bg-primary-500/20 border-primary-500 text-white shadow-[0_0_10px_rgba(139,92,246,0.3)]' 
                      : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {activeFilter === filter.id && <CheckCircle2 className="w-3 h-3" />}
                  {filter.label}
                </button>
              ))}
            </div>

            {loading && (
              <div className="mt-8 flex flex-col items-center animate-fade-in">
                 <div className="flex items-center gap-2 text-primary-300 text-sm font-medium animate-pulse">
                    <Sparkles className="w-4 h-4" /> Scouting the web...
                 </div>
                 <div className="mt-6 flex flex-col md:flex-row gap-4 w-full opacity-50">
                    <div className="w-full h-40 bg-white/5 rounded-2xl animate-pulse"></div>
                    <div className="hidden md:block w-full h-40 bg-white/5 rounded-2xl animate-pulse delay-100"></div>
                 </div>
              </div>
            )}

            {!searched && !loading && (
              <div className="mt-12 flex flex-col items-center animate-fade-in delay-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-6">Trusted by teams at</p>
                <div className="flex gap-8 opacity-40 grayscale mix-blend-screen items-center justify-center flex-wrap">
                  <div className="text-lg font-bold font-serif">ACME</div>
                  <div className="text-lg font-bold tracking-tighter">inter<span className="font-light">sect</span></div>
                  <div className="text-lg font-bold italic">Vortex</div>
                  <div className="text-lg font-bold font-mono">CODEblock</div>
                </div>
                
                <div className="mt-10 flex items-center gap-3 bg-white/5 px-4 py-2 rounded-full border border-white/5">
                  <div className="flex -space-x-2">
                    {users.map((src, i) => (
                      <img key={i} src={src} alt="User" className="w-6 h-6 rounded-full border border-surface-900" />
                    ))}
                  </div>
                  <span className="text-xs text-slate-400"><span className="text-white font-medium">12,042</span> searches today</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        {searched && (
          <div ref={resultsRef} className="w-full pb-20 animate-slide-up">
            
            {!loading && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 px-2 border-b border-white/5 pb-4 gap-4">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Zap className="w-4 h-4 text-accent-cyan" />
                  <span>Found {results.length} solutions for <span className="text-white font-medium">"{query}"</span></span>
                </div>
                
                <div className="flex gap-3 self-end sm:self-auto items-center">
                    {/* Comparison Matrix Toggle (Cognitive Offloading) */}
                    <div className="flex bg-white/5 rounded-lg p-1 border border-white/5">
                        <button 
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded transition-all ${viewMode === 'grid' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                            title="Grid View"
                        >
                            <LayoutGrid className="w-4 h-4"/>
                        </button>
                        <button 
                            onClick={() => setViewMode('table')}
                            className={`p-1.5 rounded transition-all ${viewMode === 'table' ? 'bg-white/10 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                            title="Comparison Table"
                        >
                            <TableIcon className="w-4 h-4"/>
                        </button>
                    </div>

                   <button onClick={shareSearch} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs text-slate-300 flex items-center gap-2 transition-colors border border-white/5">
                     <Share2 className="w-3 h-3"/> Share
                   </button>
                </div>
              </div>
            )}

            {!loading && results.length > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr"> 
                {results.map((tool, idx) => (
                  <div 
                   key={idx} 
                   className={`group glass-card rounded-2xl p-6 flex flex-col hover:bg-white/5 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/10 border-transparent hover:border-primary-500/30 relative overflow-hidden h-full ${idx === 0 ? 'md:col-span-2 lg:col-span-2 bg-primary-500/5 border-primary-500/20' : ''}`}
                   style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    {idx === 0 && (
                      <div className="absolute top-0 left-0 bg-primary-500 text-white text-[10px] font-bold px-3 py-1 rounded-br-xl flex items-center gap-1 z-10 shadow-lg shadow-primary-500/20">
                        <Crown className="w-3 h-3 fill-current" /> TOP PICK
                      </div>
                    )}

                    <div className="absolute top-0 right-0 p-4 z-20">
                      <button 
                        onClick={() => toggleSave(tool.url)}
                        className={`p-2 rounded-full transition-all ${savedTools.includes(tool.url) ? 'bg-rose-500/20 text-rose-400' : 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'}`}
                      >
                        <Heart className={`w-4 h-4 ${savedTools.includes(tool.url) ? 'fill-current' : ''}`} />
                      </button>
                    </div>

                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-4 mt-2">
                        <div className="flex flex-col">
                           <span className="text-[10px] font-bold uppercase tracking-wider text-primary-400 mb-1">{tool.category}</span>
                           <h2 className={`font-semibold text-white tracking-tight flex items-center gap-2 ${idx === 0 ? 'text-3xl' : 'text-xl'}`}>
                             {tool.name}
                           </h2>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                            tool.pricing === 'Free' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                            'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                          }`}>
                            {tool.pricing}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 stroke-none" /> {tool.trustScore} Trust
                          </span>
                          {tool.platforms && tool.platforms.map((p, i) => (
                             <span key={i} className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/5 text-slate-400 border border-white/10 flex items-center gap-1">
                                <Monitor className="w-3 h-3" /> {p}
                             </span>
                          ))}
                          {tool.hasDeal && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1 animate-pulse">
                                <Clock className="w-3 h-3" /> Deal ends: {tool.dealExpiry}
                            </span>
                          )}
                      </div>
                      
                      <p className={`text-slate-300 mb-6 leading-relaxed opacity-90 ${idx === 0 ? 'text-lg' : 'text-sm'}`}>{tool.description}</p>
                      
                      {/* Editor's Love Note (Human Curator) */}
                      {idx === 0 && tool.editorLoveNote && (
                          <div className="mb-6 p-4 bg-primary-500/10 rounded-xl border border-primary-500/20 relative">
                              <div className="absolute -top-2 left-4 px-2 bg-surface-800 text-[10px] text-primary-300 font-bold uppercase tracking-wider border border-primary-500/20 rounded">
                                  Editor's Choice
                              </div>
                              <p className="text-sm text-primary-100 italic">"{tool.editorLoveNote}"</p>
                          </div>
                      )}

                      {/* Negative Social Proof (Warning) */}
                      {tool.warning && (
                          <div className="mb-6 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20 flex gap-3 items-start">
                              <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5"/>
                              <div className="flex flex-col">
                                  <span className="text-[10px] font-bold text-yellow-500 uppercase">Heads up</span>
                                  <p className="text-xs text-yellow-200/80">{tool.warning}</p>
                              </div>
                          </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="space-y-1">
                           <p className="text-[10px] text-emerald-400 font-bold uppercase">Pros</p>
                           {tool.pros?.slice(0, 2).map((pro, i) => (
                             <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400">
                               <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                               <span className="leading-tight">{pro}</span>
                             </div>
                           ))}
                        </div>
                         <div className="space-y-1">
                           <p className="text-[10px] text-rose-400 font-bold uppercase">Cons</p>
                           {tool.cons?.slice(0, 1).map((con, i) => (
                             <div key={i} className="flex items-start gap-1.5 text-xs text-slate-400">
                               <AlertCircle className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" />
                               <span className="leading-tight">{con}</span>
                             </div>
                           ))}
                        </div>
                      </div>

                      {tool.alternativeTo && (
                        <div className="mb-4 text-xs text-slate-500 bg-white/5 p-2 rounded-lg border border-white/5 text-center">
                          Simpler alternative to <span className="text-white font-medium">{tool.alternativeTo}</span>
                        </div>
                      )}
                      
                      <div className="bg-white/5 rounded-xl p-3 mb-6 border border-white/5">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-slate-300">
                            <span className="text-white font-medium">Why it fits:</span> {tool.matchReason}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto pt-4 border-t border-white/5 flex gap-2">
                        <button onClick={() => shareResult(tool)} className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-slate-300 transition-colors" title="Share Tool">
                            <Share2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setShowVideoModal(tool.name)} className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-slate-300 transition-colors flex-grow flex items-center justify-center gap-2 text-xs font-medium">
                            <PlayCircle className="w-4 h-4" /> Watch Demo
                        </button>
                        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white text-black rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary-50 transition-all group-hover:shadow-lg transform group-hover:-translate-y-0.5 flex-grow">
                            Visit <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Comparison Matrix View */}
            {!loading && results.length > 0 && viewMode === 'table' && (
                <div className="overflow-x-auto rounded-2xl border border-white/10 bg-surface-900/50 backdrop-blur-sm animate-fade-in">
                    <table className="w-full text-left text-sm text-slate-400">
                        <thead className="bg-white/5 text-slate-200 font-medium border-b border-white/10">
                            <tr>
                                <th className="p-4 w-1/4">Tool</th>
                                <th className="p-4">Price</th>
                                <th className="p-4">Watermark?</th>
                                <th className="p-4">Limit</th>
                                <th className="p-4">Card Req?</th>
                                <th className="p-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {results.map((tool, idx) => (
                                <tr key={idx} className="hover:bg-white/5 transition-colors">
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <span className={`font-semibold text-white ${idx === 0 ? 'text-primary-400' : ''}`}>{tool.name} {idx === 0 && '👑'}</span>
                                            <span className="text-xs opacity-70">{tool.category}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                                            tool.pricing === 'Free' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                                            'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                                          }`}>
                                            {tool.pricing}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        {tool.specs?.watermark ? <span className="text-rose-400">Yes</span> : <span className="text-emerald-400">No</span>}
                                    </td>
                                    <td className="p-4 font-mono text-xs text-white">
                                        {tool.specs?.exportLimit || "Unknown"}
                                    </td>
                                    <td className="p-4">
                                        {tool.specs?.requiresCard ? <span className="text-orange-400">Yes</span> : <span className="text-slate-500">No</span>}
                                    </td>
                                    <td className="p-4 text-right">
                                        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-white font-medium flex items-center gap-1 justify-end">
                                            Visit <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            
            {!loading && results.length === 0 && (
               <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/5 flex flex-col items-center">
                 <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                   <Search className="w-8 h-8 text-slate-600" />
                 </div>
                 <h3 className="text-lg font-medium text-white mb-2">No perfect match found.</h3>
                 <p className="text-slate-400 mb-6 max-w-sm">Try broadening your search or removing filters.</p>
                 <button onClick={() => { setQuery(''); setSearched(false); window.history.pushState({}, '', '/'); }} className="px-6 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-slate-200 transition-colors">Search Again</button>
               </div>
            )}
          </div>
        )}

        {!searched && (
          <div className="w-full mt-24 pt-10 border-t border-white/5 animate-fade-in pb-10">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <TrendingUp className="w-3 h-3" /> Trending Searches (24h)
                </h3>
                <span className="text-[10px] text-slate-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Live</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { label: 'Remove Background from PDF', cat: 'PDF', hot: true },
                { label: 'Compress 4K Video', cat: 'Video', hot: false },
                { label: 'Generate Logo AI', cat: 'Design', hot: true },
                { label: 'Convert Python to JS', cat: 'Dev', hot: false },
                { label: 'Free Voice Changer', cat: 'Audio', hot: false },
                { label: 'Upscale Images 4x', cat: 'Image', hot: true },
                { label: 'Transcribe Audio to Text', cat: 'AI', hot: false },
                { label: 'Find Email Address', cat: 'Sales', hot: true }
              ].map((item, i) => (
                <button 
                  key={i}
                  onClick={() => { performSearch(item.label); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="p-4 rounded-xl glass border border-white/5 text-left hover:bg-white/10 hover:border-primary-500/30 transition-all duration-300 group relative overflow-hidden"
                >
                  <div className="flex justify-between items-start mb-1">
                      <span className="block text-[10px] text-primary-400">{item.cat}</span>
                      {item.hot && <span className="text-[9px] font-bold text-orange-400 flex items-center gap-0.5"><Flame className="w-2.5 h-2.5" /> HOT</span>}
                  </div>
                  <span className="text-sm text-slate-200 group-hover:text-white font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

      </main>

      <footer className="mt-auto border-t border-white/5 bg-background/50 backdrop-blur-lg pt-12 pb-16 px-6 relative">
         <div className="absolute bottom-0 left-0 right-0 h-8 bg-primary-900/20 border-t border-primary-500/10 overflow-hidden flex items-center">
            <div className="flex animate-ticker whitespace-nowrap gap-12 px-4">
              {seamlessTickerItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] text-primary-300/80">
                   <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                   <span className="font-medium text-primary-200">Someone in {item.location}</span> found <span className="text-white">{item.tool}</span> <span className="opacity-50">{item.time}</span>
                </div>
              ))}
            </div>
         </div>

        <div className="max-w-7xl mx-auto mb-8">
           <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <div className="col-span-2 md:col-span-1">
                 <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-primary-500" />
                    <span className="font-semibold text-white">SiteFinder</span>
                 </div>
                 <p className="text-sm text-slate-500 leading-relaxed">
                   The intelligent search engine for creators. Find the perfect tools for any task in seconds.
                 </p>
              </div>
              
              {/* SEO Growth Engine: Community Searches (Live Index) */}
              <div className="col-span-2">
                 <h4 className="text-white font-medium mb-4 text-sm flex items-center gap-2">
                    <Globe className="w-3 h-3 text-primary-400" /> Community Searches (Live Index)
                 </h4>
                 <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {dailyKeywords.map((kw, i) => (
                        <a 
                            key={i} 
                            href={`/?q=${encodeURIComponent(kw)}`}
                            className="text-[11px] text-slate-500 hover:text-primary-400 hover:underline truncate block"
                            title={`Find tools for ${kw}`}
                        >
                            {kw}
                        </a>
                    ))}
                 </div>
              </div>

              <div>
                 <h4 className="text-white font-medium mb-4 text-sm">Company</h4>
                 <ul className="space-y-2 text-sm text-slate-500">
                    <li><button onClick={() => setLegalModal('about')} className="hover:text-primary-400">About Diffusio</button></li>
                    <li><button onClick={() => setLegalModal('privacy')} className="hover:text-primary-400">Privacy Policy</button></li>
                    <li><button onClick={() => setLegalModal('terms')} className="hover:text-primary-400">Terms of Service</button></li>
                 </ul>
              </div>
           </div>
           
           <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/5 gap-4">
              <p className="text-xs text-slate-600">© 2024 Diffusio Studios. All rights reserved.</p>
           </div>
        </div>
      </footer>

      <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
    </div>
  );
};

export default App;